import React, { useState } from "react";
import Header from "../../components/header/Header";
import { changePassword } from "../../modules/serverCall";
import Auth from "../../modules/Auth";
import Swal from "sweetalert2";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai"; // Import the eye icons

const ResetPassword = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(""); // For success or error messages

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the default form submission behavior
    setMessage(""); // Clear any existing message
    setErrors({}); // Reset validation errors

    let validationErrors = {};

    // Validation
    if (!currentPassword) {
      validationErrors.currentPassword = "Current password is required.";
    }

    if (!newPassword) {
      validationErrors.newPassword = "New password is required.";
    } else if (!/^\d{6}$/.test(newPassword)) {
      validationErrors.newPassword =
        "Password must contain ONLY numbers, 6 digits ONLY.";
    } else if (hasConsecutiveRepeats(newPassword)) {
      validationErrors.newPassword =
        "Repetition of numbers like 111, 222 is not allowed.";
    } else if (newPassword === currentPassword) {
      validationErrors.newPassword =
        "New password cannot be the same as the current password.";
    }

    if (!confirmNewPassword) {
      validationErrors.confirmNewPassword = "Confirm new password is required.";
    } else if (newPassword !== confirmNewPassword) {
      validationErrors.confirmNewPassword =
        "New password and confirm password must match.";
    } else if (!/^\d{6}$/.test(confirmNewPassword)) {
      validationErrors.confirmNewPassword =
        "Confirm password must contain ONLY numbers, 6 digits ONLY.";
    } else if (hasConsecutiveRepeats(confirmNewPassword)) {
      validationErrors.confirmNewPassword =
        "Repetition of numbers like 111, 222 is not allowed.";
    }

    // If validation fails, show errors and stop submission
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Proceed with API call to reset password
    try {
      const accesstokendata = localStorage.getItem("access_token");
      const readData = JSON.parse(accesstokendata);
      const cusId = readData.userdata.sub;

      const submitData = {
        currentPassword,
        newPassword,
        confirmNewPassword,
        cusId,
      };

      const response = await changePassword(submitData);

      console.log("response", response);

      // Handle success: show success message and prompt logout
      if (response) {
        Swal.fire({
          title: "Password changed successfully!",
          text: "Please log in again.",
          icon: "success",
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            Auth.logout(); // Call auth.logout() when the user clicks "OK"
          }
        });

        // Optionally clear form fields
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      }
    } catch (error) {
      // Handle failure: show error message
      setMessage("Error changing password, please try again.");
    }
  };

  // Function to check for consecutive repeated numbers
  const hasConsecutiveRepeats = (number) => {
    for (let i = 0; i < number.length - 1; i++) {
      if (number[i] === number[i + 1]) {
        return true;
      }
    }
    return false;
  };

  // Toggle password visibility for each field
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmNewPasswordVisibility = () => {
    setShowConfirmNewPassword(!showConfirmNewPassword);
  };

  // Helper function to ensure only numeric input
  const handleNumericInput = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  };

  return (
    <div>
      <Header />
      <div className="topcon py-5">
        <div className="row rowDataClass d-flex justify-content-center align-items-center h-100">
          <div className="col-xl-6">
            <div className="card rounded-3 text-black shadow-lg">
              <div className="card-body p-md-5 mx-md-4">
                <h3 className="text-center mb-4">Change Password</h3>

                {/* Display success or error message */}
                {message && (
                  <div
                    className={`alert ${
                      message.includes("successfully")
                        ? "alert-success"
                        : "alert-danger"
                    }`}
                    role="alert"
                  >
                    {message}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Current Password */}
                  <div className="form-group mb-2">
                    <label htmlFor="currentPassword">Current Password</label>
                    <div className="input-group">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        className={`form-control ${
                          errors.currentPassword ? "is-invalid" : ""
                        }`}
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                      <span
                        className="input-group-text"
                        onClick={toggleCurrentPasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        {showCurrentPassword ? (
                          <AiFillEyeInvisible />
                        ) : (
                          <AiFillEye />
                        )}
                      </span>
                    </div>
                    {errors.currentPassword && (
                      <div className="text-danger mt-1">
                        {errors.currentPassword}
                      </div>
                    )}
                  </div>

                  {/* New Password */}
                  <div className="form-group mb-2">
                    <label htmlFor="newPassword">New Password (6 digits)</label>
                    <div className="input-group">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        className={`form-control ${
                          errors.newPassword ? "is-invalid" : ""
                        }`}
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        maxLength="6"
                        pattern="\d{6}" // Only allow numeric values
                        title="New password must be exactly 6 numeric characters."
                        onKeyPress={handleNumericInput}
                      />
                      <span
                        className="input-group-text"
                        onClick={toggleNewPasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        {showNewPassword ? (
                          <AiFillEyeInvisible />
                        ) : (
                          <AiFillEye />
                        )}
                      </span>
                    </div>
                    {errors.newPassword && (
                      <div className="text-danger mt-1">
                        {errors.newPassword}
                      </div>
                    )}
                    <span
                      style={{
                        fontSize: "11px",
                        color: "red",
                      }}
                    >
                      Password must contain ONLY numbers, 6 digits ONLY.
                      Repetition of digits like 111 is not allowed.
                    </span>
                  </div>

                  {/* Confirm New Password */}
                  <div className="form-group mb-4">
                    <label htmlFor="confirmNewPassword">
                      Confirm New Password
                    </label>
                    <div className="input-group">
                      <input
                        type={showConfirmNewPassword ? "text" : "password"}
                        className={`form-control ${
                          errors.confirmNewPassword ? "is-invalid" : ""
                        }`}
                        id="confirmNewPassword"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        maxLength="6"
                        pattern="\d{6}" // Only allow numeric values
                        title="Confirm password must be exactly 6 numeric characters."
                        onKeyPress={handleNumericInput}
                      />
                      <span
                        className="input-group-text"
                        onClick={toggleConfirmNewPasswordVisibility}
                        style={{ cursor: "pointer" }}
                      >
                        {showConfirmNewPassword ? (
                          <AiFillEyeInvisible />
                        ) : (
                          <AiFillEye />
                        )}
                      </span>
                    </div>
                    {errors.confirmNewPassword && (
                      <div className="text-danger mt-1">
                        {errors.confirmNewPassword}
                      </div>
                    )}
                  </div>

                  <button type="submit" className="btn btn-primary w-100">
                    Submit
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
